<template>
  <div class="index container">
    <transition name="fade">
      <router-view @showParent="showParent"> </router-view>
    </transition>

    <div v-if="showParentPage">
      <v-breadcrumbs>
        <v-breadcrumbs-item :exact="true" :to="{ name: 'home', params: {} }">Home</v-breadcrumbs-item>
        <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
        <v-breadcrumbs-item :to="{ name: 'inventoryReservationDashboard' }">Inventory Reservations</v-breadcrumbs-item>
        <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
        <v-breadcrumbs-item active>All Reservations</v-breadcrumbs-item>
      </v-breadcrumbs>

      <!-- Filter Panel -->
      <v-expansion-panels flat class="mb-4">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <span><v-icon small class="mr-1">mdi-filter</v-icon> Filters</span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row dense>
              <v-col cols="12" md="3">
                <v-text-field
                  dense v-model="searchTerms" outlined clearable
                  label="Search" placeholder="Product or document #"
                  append-icon="mdi-magnify"
                  @keyup.enter="search" @click:clear="clearSearch" @click:append="search"
                />
              </v-col>
              <v-col cols="6" md="2">
                <v-text-field
                  dense v-model.number="filterWorksheetId" outlined clearable type="number"
                  label="Worksheet ID"
                  @keyup.enter="search" @click:clear="search"
                />
              </v-col>
              <v-col cols="6" md="2">
                <v-text-field
                  dense v-model.number="filterInventoryId" outlined clearable type="number"
                  label="Inventory ID"
                  @keyup.enter="search" @click:clear="search"
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-select
                  dense v-model="filterWorksheetStatus" outlined clearable
                  label="Worksheet Status"
                  :items="['PENDING', 'SUBMITTED']"
                  @change="search"
                />
              </v-col>
              <v-col cols="6" md="1">
                <v-switch v-model="filterStaleOnly" label="Stale" dense class="mt-1" @change="search" />
              </v-col>
              <v-col cols="6" md="2">
                <v-btn text color="grey" @click="clearFilters">Clear</v-btn>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- Bulk Actions Toolbar -->
      <v-toolbar flat dense color="transparent" v-if="selected.length > 0" class="mb-2">
        <v-chip color="primary" small class="mr-2">{{ selected.length }} selected</v-chip>
        <v-btn small color="warning" outlined @click="bulkRelease">
          <v-icon small left>mdi-lock-open-outline</v-icon>
          Release Selected
        </v-btn>
        <v-spacer />
        <v-btn small text @click="selected = []">Clear Selection</v-btn>
      </v-toolbar>

      <div class="table-container">
        <v-data-table
          :headers="headers"
          :items="inventoryReservationList"
          :items-per-page="pagination.pageSize"
          :loading="!inventoryReservationProcessed"
          :server-items-length="pagination.totalSize"
          :page="pagination.current"
          :disable-pagination="!inventoryReservationList || inventoryReservationList.length < 1"
          :hide-default-header="!inventoryReservationList || inventoryReservationList.length < 1"
          :hide-default-footer="!inventoryReservationList || inventoryReservationList.length < 1"
          :options.sync="options"
          v-model="selected"
          show-select
          item-key="id"
          class="elevation-1"
          @update:options="pageChanged"
          :item-class="rowClass"
        >
          <template v-slot:item.processWorksheetId="{ item }">
            <a @click="openWorksheet(item.processWorksheetId)">
              {{ item.worksheetName }} {{ item.processWorksheetId }}
            </a>
            <div class="text-caption" v-if="item.processName">{{ item.processName }}</div>
            <v-chip v-if="item.worksheetStatus" x-small outlined
              :color="item.worksheetStatus === 'PENDING' ? 'orange' : 'green'" class="mt-1">
              {{ item.worksheetStatus }}
            </v-chip>
          </template>
          <template v-slot:item.inventoryId="{ item }">
            <a @click="openInventory(item.inventoryId)">{{ item.inventoryId }}</a>
            <span class="text-caption" v-if="item.inventoryUnitId"> / {{ item.inventoryUnitCode }}</span>
          </template>
          <template v-slot:item.createTime="{ item }">
            {{ item.createTime | formatDateYear }}
          </template>
          <template v-slot:item.quantity="{ item }">
            {{ item.quantity | formatNumber }}
          </template>
          <template v-slot:item.age="{ item }">
            <v-chip x-small :color="ageColor(item.createTime)" dark>
              {{ ageText(item.createTime) }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-icon small class="mr-1" @click="openInventory(item.inventoryId)" title="View Inventory">
              mdi-information-outline
            </v-icon>
            <v-icon small class="mr-1" @click="openWorksheet(item.processWorksheetId)" title="View Worksheet">
              mdi-file-document-outline
            </v-icon>
            <v-icon small class="mr-1" @click="releaseSingle(item)" title="Release" color="warning">
              mdi-lock-open-outline
            </v-icon>
            <v-icon small @click="remove(item)" title="Delete" color="error">
              mdi-delete
            </v-icon>
          </template>
        </v-data-table>
      </div>
    </div>
    <confirmation-dialog ref="confirm" />
    <inventory-lookup :activated.sync="lookupInventory" v-model="selectedInventory" />

    <v-dialog v-model="worksheetDialog" style="z-index: 1000">
      <v-card>
        <v-card-title class="justify-end">
          <v-btn @click="worksheetDialog = null" icon><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <worksheet-card :worksheet="selectedWorksheet" :key="selectedWorksheet" :showEditButtons="false" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
const InventoryLookup = () => import("@/components/inventory/InventoryLookup.vue");
const WorksheetCard = () => import("@/components/processWorksheet/WorksheetCard.vue");

export default {
  data() {
    return {
      pagination: { current: 1, pageSize: 10, totalSize: 100 },
      options: { sortBy: [], sortDesc: [] },
      inventoryReservationList: [],
      showParentPage: true,
      selected: [],
      filterWorksheetId: null,
      filterInventoryId: null,
      filterWorksheetStatus: null,
      filterStaleOnly: false,
      headers: [
        { text: "Worksheet", value: "processWorksheetId", align: "start", sortable: true },
        { text: "Product", value: "productName", align: "start", sortable: false },
        { text: "Inventory / (Unit)", value: "inventoryId", align: "start", sortable: true },
        { text: "Quantity", value: "quantity", align: "end", sortable: true },
        { text: "Reserved On", value: "createTime", align: "start", sortable: true },
        { text: "Age", value: "age", align: "center", sortable: false },
        { text: "Actions", value: "actions", align: "end", sortable: false },
      ],
      sort: { key: null },
      searchTerms: null,
      lookupInventory: false,
      selectedInventory: null,
      worksheetDialog: false,
      selectedWorksheet: null,
    };
  },
  components: { InventoryLookup, WorksheetCard },
  mounted() {
    this.getResources();
  },
  computed: {
    ...mapGetters("inventoryReservation", ["inventoryReservationProcessed", "getInventoryReservations"]),
  },
  methods: {
    ...mapActions("messages", ["addErrorMessage", "addMessage", "addSuccessMessage"]),
    ...mapActions("inventoryReservation", ["deleteInventoryReservation", "fetchInventoryReservations"]),
    ageText(createTime) {
      if (!createTime) return '';
      const now = new Date();
      const created = new Date(createTime);
      const diffMs = now - created;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays < 1) return '<1d';
      if (diffDays < 7) return diffDays + 'd';
      if (diffDays < 30) return Math.floor(diffDays / 7) + 'w';
      return Math.floor(diffDays / 30) + 'mo';
    },
    ageColor(createTime) {
      if (!createTime) return 'grey';
      const diffDays = Math.floor((new Date() - new Date(createTime)) / (1000 * 60 * 60 * 24));
      if (diffDays < 3) return 'success';
      if (diffDays < 7) return 'warning';
      return 'error';
    },
    rowClass(item) {
      if (!item.createTime) return '';
      const diffDays = Math.floor((new Date() - new Date(item.createTime)) / (1000 * 60 * 60 * 24));
      if (diffDays >= 7 && item.worksheetStatus === 'PENDING') return 'stale-row';
      return '';
    },
    openWorksheet(worksheetId) {
      this.worksheetDialog = true;
      this.selectedWorksheet = worksheetId;
    },
    openInventory(inventoryId) {
      this.selectedInventory = inventoryId;
      this.lookupInventory = true;
    },
    async remove(item) {
      let confirm = await this.$refs.confirm.open("Confirm Delete", "Do you want to delete this inventory reservation?");
      if (confirm) {
        this.deleteInventoryReservation(item.id)
          .then(() => { this.addSuccessMessage("Reservation deleted"); this.getResources(); })
          .catch(() => { this.addErrorMessage("Server response with error"); });
      }
    },
    async releaseSingle(item) {
      let confirm = await this.$refs.confirm.open("Release Reservation", `Release reservation of ${item.quantity} kg on inventory ${item.inventoryId}?`);
      if (confirm) {
        this.deleteInventoryReservation(item.id)
          .then(() => { this.addSuccessMessage("Reservation released"); this.getResources(); })
          .catch(() => { this.addErrorMessage("Failed to release reservation"); });
      }
    },
    async bulkRelease() {
      let confirm = await this.$refs.confirm.open("Bulk Release", `Release ${this.selected.length} selected reservations?`);
      if (confirm) {
        const promises = this.selected.map(r => this.deleteInventoryReservation(r.id));
        Promise.all(promises)
          .then(() => {
            this.addSuccessMessage(`${this.selected.length} reservations released`);
            this.selected = [];
            this.getResources();
          })
          .catch(() => { this.addErrorMessage("Some reservations failed to release"); });
      }
    },
    getResources() {
      const sortBy = this.options.sortBy && this.options.sortBy.length > 0 ? this.options.sortBy[0] : "id";
      const sortDesc = this.options.sortDesc && this.options.sortDesc.length > 0 ? this.options.sortDesc[0] : true;
      const params = {
        page: this.pagination.current > 0 ? this.pagination.current - 1 : 0,
        size: this.pagination.pageSize,
        sort: `${sortBy}${sortDesc ? ",desc" : ""}`,
        productName: this.searchTerms || undefined,
        worksheetStatus: this.filterWorksheetStatus || undefined,
        worksheetId: this.filterWorksheetId || undefined,
        inventoryId: this.filterInventoryId || undefined,
      };
      this.fetchInventoryReservations(params)
        .then((response) => {
          if (response) {
            this.pagination.totalSize = response.totalElements;
            let list = response.content;
            if (this.filterStaleOnly) {
              const cutoff = new Date();
              cutoff.setDate(cutoff.getDate() - 7);
              list = list.filter(r => new Date(r.createTime) < cutoff);
            }
            this.inventoryReservationList = list;
          }
        })
        .catch((error) => { this.addErrorMessage("Server response with error " + error); });
    },
    showParent(show) { this.showParentPage = show; },
    pageChanged(newOptions) {
      this.options = newOptions;
      this.pagination.current = newOptions.page;
      this.pagination.pageSize = newOptions.itemsPerPage;
      this.getResources();
    },
    async search() {
      this.pagination.current = 1;
      this.options.page = 1;
      await this.getResources();
    },
    clearSearch() { this.searchTerms = null; this.getResources(); },
    clearFilters() {
      this.searchTerms = null;
      this.filterWorksheetId = null;
      this.filterInventoryId = null;
      this.filterWorksheetStatus = null;
      this.filterStaleOnly = false;
      this.search();
    },
  },
  watch: {
    "pagination.numberRegisterForPage": function () { this.pagination.current = -1; },
  },
  beforeRouteUpdate(to, from, next) {
    const toDepth = to.path.split("/").length;
    const fromDepth = from.path.split("/").length;
    this.showParentPage = toDepth < fromDepth;
    if (this.showParentPage) this.getResources();
    next();
  },
};
</script>

<style scoped>
a { color: blue !important; }
.stale-row { background-color: #FFF3E0 !important; }
</style>
